import { logDev } from '../index';

export class ClipboardManager {
    constructor(private app: any) { }

    hasSelection(): boolean {
        return !!(this.app.pastedData || this.app.selectedFrame);
    }

    async copyToClipboard(suppressDeselect = false) {
        const dataCanvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
        if (!dataCanvas) return;

        const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fw = parseInt(wInput.value) || 32;
        const fh = parseInt(hInput.value) || 32;

        let sourceCanvas: HTMLCanvasElement;

        if (this.app.pastedData) {
            // Priority 1: Floating Paste
            sourceCanvas = document.createElement('canvas');
            sourceCanvas.width = this.app.pastedData.w;
            sourceCanvas.height = this.app.pastedData.h;
            const ctx = sourceCanvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(this.app.pastedData.img, 0, 0, this.app.pastedData.w, this.app.pastedData.h);
            }
        } else if (this.app.selectedFrame) {
            // Priority 2: Selected Frame
            sourceCanvas = document.createElement('canvas');
            sourceCanvas.width = fw;
            sourceCanvas.height = fh;
            const ctx = sourceCanvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(dataCanvas, this.app.selectedFrame.col * fw, this.app.selectedFrame.row * fh, fw, fh, 0, 0, fw, fh);
            }
        } else {
            // Priority 3: Whole Sheet
            sourceCanvas = dataCanvas;
        }

        return new Promise<void>((resolve, reject) => {
            try {
                sourceCanvas.toBlob(async (blob) => {
                    if (blob) {
                        try {
                            const data = [new ClipboardItem({ [blob.type]: blob })];
                            await navigator.clipboard.write(data);
                            logDev(`[Clipboard] Copied ${this.app.selectedFrame ? 'frame' : 'sheet'} to clipboard`);

                            if (!suppressDeselect && this.app.selectedFrame) {
                                this.app.selectedFrame = null;
                                const fw = parseInt((this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value) || 32;
                                const fh = parseInt((this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value) || 32;
                                this.app.updateSelectionHighlight(fw, fh);
                            }
                            resolve();
                        } catch (writeErr) {
                            console.error('Failed to write to clipboard:', writeErr);
                            reject(writeErr);
                        }
                    } else {
                        reject(new Error('Failed to create blob from canvas'));
                    }
                });
            } catch (err) {
                console.error('Failed to copy image to clipboard:', err);
                reject(err);
            }
        });
    }

    async cutToClipboard() {
        if (!this.app.dataCanvas) return;

        // 1. Copy (suppress deselect so we can cut the selection area)
        await this.copyToClipboard(true);

        // 2. Clear
        if (this.app.pastedData) {
            // Priority 1: Remove Floating Paste
            this.app.pastedData = null;
            this.app.updatePasteOverlay();
            logDev(`[Clipboard] Cut floating paste`);
        } else {
            // Priority 2/3: Clear Canvas Area
            const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
            const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
            const fw = parseInt(wInput.value) || 32;
            const fh = parseInt(hInput.value) || 32;

            const ctx = this.app.dataCanvas.getContext('2d');
            if (ctx) {
                if (this.app.selectedFrame) {
                    ctx.clearRect(this.app.selectedFrame.col * fw, this.app.selectedFrame.row * fh, fw, fh);
                } else {
                    ctx.clearRect(0, 0, this.app.dataCanvas.width, this.app.dataCanvas.height);
                }

                this.app.currentBase64 = this.app.dataCanvas.toDataURL();
                this.app.updatePreview(this.app.currentBase64, false);
                this.app.historyManager.takeSnapshot();
                this.app.updateButtonStates();

                if (this.app.selectedFrame) {
                    this.app.selectedFrame = null;
                    this.app.updateSelectionHighlight(fw, fh);
                }

                logDev(`[Clipboard] Cut ${this.app.selectedFrame ? 'frame' : 'sheet'} to clipboard`);
            }
        }
    }

    async pasteFromClipboard() {
        try {
            // Commit active floating paste
            if (this.app.pastedData) {
                this.commitPaste();
            }

            // Deselect before pasting
            if (this.app.selectedFrame) {
                this.app.selectedFrame = null;
                const fw = parseInt((this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value) || 32;
                const fh = parseInt((this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value) || 32;
                this.app.updateSelectionHighlight(fw, fh);
            }

            const items = await navigator.clipboard.read();
            for (const item of items) {
                const imageType = item.types.find((type: string) => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    const img = new Image();
                    img.onload = () => {
                        this.app.pastedData = {
                            img,
                            x: this.app.lastSheetX !== -1 ? this.app.lastSheetX - img.width / 2 : 0,
                            y: this.app.lastSheetY !== -1 ? this.app.lastSheetY - img.height / 2 : 0,
                            w: img.width,
                            h: img.height,
                            activeHandle: null,
                            startX: 0,
                            startY: 0,
                            startW: img.width,
                            startH: img.height,
                            startPosX: 0,
                            startPosY: 0,
                            startAncX: 0,
                            startAncY: 0,
                            startLX: 0,
                            startLY: 0,
                            rotation: 0,
                            startRotation: 0
                        };
                        const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
                        if (canvas) this.app.renderToolPreview(this.app.lastSheetX, this.app.lastSheetY, canvas, this.app.dataCanvas);
                        this.app.editorManager.updatePasteOverlay();
                        logDev(`[Clipboard] Pasted image from clipboard`);
                    };
                    img.src = URL.createObjectURL(blob);
                }
            }
        } catch (err) {
            console.error('Failed to paste image from clipboard:', err);
        }
    }

    commitPaste() {
        if (!this.app.pastedData || !this.app.dataCanvas) return;

        const ctx = this.app.dataCanvas.getContext('2d');
        if (ctx) {
            ctx.imageSmoothingEnabled = false;

            ctx.save();
            const cx = this.app.pastedData.x + this.app.pastedData.w / 2;
            const cy = this.app.pastedData.y + this.app.pastedData.h / 2;
            ctx.translate(cx, cy);
            ctx.rotate(this.app.pastedData.rotation);
            ctx.drawImage(this.app.pastedData.img, -this.app.pastedData.w / 2, -this.app.pastedData.h / 2, this.app.pastedData.w, this.app.pastedData.h);
            ctx.restore();

            this.app.pastedData = null;
            this.app.currentBase64 = this.app.dataCanvas.toDataURL();
            this.app.updatePreview(this.app.currentBase64, false);
            this.app.historyManager.takeSnapshot();
            logDev(`[Clipboard] Committed pasted image`);
        }
    }
}
